import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const SORTS = [
  { value: "popular", label: "Popular" },
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");

  const category = params.get("category") || "";
  const sort = params.get("sort") || "popular";
  const q = params.get("search") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: { category: category || undefined, search: q || undefined, sort } })
      .then((r) => setProducts(r.data.products))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, q, sort]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-28 md:pt-36 pb-24" data-testid="shop-page">
      <p className="text-xs uppercase tracking-[0.25em] text-brand-blue font-semibold">The Collection</p>
      <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-3">Shop Vearn</h1>

      <div className="mt-10 flex flex-col lg:flex-row lg:items-center gap-4">
        <form onSubmit={(e) => { e.preventDefault(); setParam("search", search.trim()); }} className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink/40" />
          <input
            data-testid="shop-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search the collection..."
            className="w-full h-12 rounded-full bg-brand-ink/5 pl-11 pr-4 text-sm outline-none focus:ring-2 focus:ring-brand-cyan"
          />
        </form>
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <button data-testid="filter-all" onClick={() => setParam("category", "")}
            className={`shrink-0 rounded-full px-5 h-10 text-sm font-medium transition-colors ${!category ? "brand-gradient-bg text-white" : "bg-brand-ink/5 hover:bg-brand-ink/10"}`}>
            All
          </button>
          {categories.map((row) => {
            const c = typeof row === "string" ? row : row.name;
            return <button key={c} data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setParam("category", c)}
              className={`shrink-0 rounded-full px-5 h-10 text-sm font-medium transition-colors ${category === c ? "brand-gradient-bg text-white" : "bg-brand-ink/5 hover:bg-brand-ink/10"}`}>
              {typeof row === "string" ? c : `${c} (${row.count})`}
            </button>
          })}
        </div>
        <div className="flex items-center gap-2 lg:ml-auto">
          <SlidersHorizontal className="h-4 w-4 text-brand-ink/40" />
          <select data-testid="shop-sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)}
            className="h-10 rounded-full bg-brand-ink/5 px-4 text-sm outline-none focus:ring-2 focus:ring-brand-cyan">
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mt-12">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse"><div className="aspect-square rounded-2xl bg-brand-ink/5" /><div className="h-4 bg-brand-ink/5 rounded mt-4 w-3/4" /></div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-32" data-testid="shop-empty">
          <p className="font-display text-2xl font-semibold text-brand-ink/60">Nothing found</p>
          <p className="text-sm text-brand-ink/40 mt-2">Try a different search or category.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mt-12" data-testid="shop-grid">
          {products.map((p, i) => <ProductCard key={p.id} product={p} tall={i % 5 === 2} />)}
        </div>
      )}
    </div>
  );
}
